import { fetchEventSource } from '@microsoft/fetch-event-source';
import { API } from '~apis/axios/instance';
import { ResponseBaseWithData } from '~types/response';

// 대화 생성 요청/응답 타입
export interface ConversationCreateResponse {
  conversationId: string;
}

// 메시지 전송 요청 타입 (백엔드 ChatRequest와 동일)
export interface ChatRequest {
  conversationId: string;
  content: string;
  currentCompanyId: string;
  pageContext?: string; // 페이지 컨텍스트 정보
}

// API 요청/응답 타입 정의
export interface CommandAnswer {
  performBrowserTask?: {
    thinking: string;
    evaluationPreviousGoal: string;
    memory: string;
    nextGoal: string;
    taskComplete: boolean;
    actions: Array<{
      type: 'click' | 'focus' | 'scroll' | 'input';
      id: string;
      value?: string;
    }>;
  };
}

export interface ChatResponse {
  plainTextAnswer?: {
    text: string;
  };
  commandAnswer?: CommandAnswer;
}

// 브라우저 액션 관련 타입들
export interface BrowserAction {
  type: 'click' | 'focus' | 'scroll' | 'input';
  id?: string;
  value?: string;
}

export interface BrowserTask {
  thinking: string;
  evaluationPreviousGoal: string;
  memory: string;
  nextGoal: string;
  taskComplete: boolean;
  actions: BrowserAction[];
}

/**
 * 새로운 대화를 생성하는 API 함수
 */
export const createConversation = () => {
  return API.post<ResponseBaseWithData<ConversationCreateResponse>>(
    '/api/v1/ai-assistant/conversation',
  );
};

/**
 * 대화에 메시지를 보내는 API 함수
 */
export const sendMessageToConversation = async (
  request: ChatRequest,
  handlers: {
    onMessage?: (data: ChatResponse) => void;
    onClose?: () => void;
    onError?: (error: any) => void;
  },
): Promise<void> => {
  try {
    return await fetchEventSource(
      `/proxy-api/api/v1/ai-assistant/conversation/${request.conversationId}/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        onmessage(event) {
          try {
            const data = JSON.parse(event.data) as ChatResponse;
            handlers.onMessage?.(data);
          } catch (e) {
            console.error('Error parsing SSE data:', e);
            handlers.onError?.(e);
          }
        },
        onclose() {
          handlers.onClose?.();
        },
        onerror(err) {
          console.error('SSE error:', err);
          handlers.onError?.(err);
        },
      },
    );
  } catch (error) {
    console.error('Error sending message:', error);
    handlers.onError?.(error);
  }
};
